import React from "react";
import PageLayout, { SectionReveal } from "../components/PageLayout";
import PageHero from "../components/PageHero";
import PageBanner from "../components/PageBanner";
import { FileText, Calendar, Sheet, Database, Briefcase, Cloud, Check, ArrowRight, ExternalLink } from "lucide-react";

const EnterpriseAssistantPage = () => {
  const integrations = [
    {
      icon: FileText,
      title: "Google Docs",
      desc: "Dokümanları sesli komutla oluşturun, düzenleyin ve paylaşın. Toplantı notlarını otomatik Google Doc'a dönüştürün.",
    },
    {
      icon: Sheet,
      title: "Google Sheets / Excel",
      desc: "Veri girişi, formül oluşturma ve rapor hazırlama işlemlerini sesli komutla yönetin. Haftalık satış raporunu otomatik oluşturun.",
    },
    {
      icon: Calendar,
      title: "Google Calendar / Outlook",
      desc: "Toplantı planlayın, müsaitlik kontrol edin, katılımcılara davet gönderin. Çakışmaları otomatik tespit edip alternatif önerin.",
    },
    {
      icon: Database,
      title: "Veritabanı & CRM Sorgulama",
      desc: "SQL sorgusu yazmadan veritaban\u0131n\u0131zdan rapor al\u0131n. \u201CGe\u00E7en ay\u0131n en \u00E7ok satan \u00FCr\u00FCnleri\u201D gibi do\u011Fal dil sorgular\u0131yla \u00E7al\u0131\u015F\u0131r.",
    },
    {
      icon: Briefcase,
      title: "Slack / Teams Entegrasyonu",
      desc: "Kanal mesajlarını özetleyin, toplantı notlarını paylaşın, görev atayın. Ekip iletişimini AI asistanla otomatikleştirin.",
    },
    {
      icon: Cloud,
      title: "API Bağlantı Noktası",
      desc: "Jira, Notion, Trello, Asana ve 100+ uygulamaya bağlanır. Rutin görevleri tetikleyin, durum güncelleyin, rapor alın.",
    },
  ];

  const hero = (
    <PageHero
      eyebrow="Ürün"
      title="Kurumsal Çok Yönlü Asistan"
      subtitle="Google Workspace, Microsoft 365, Slack ve daha fazlasına bağlanan; rutin işlerinizi sesli veya yazılı komutlarla yöneten kurumsal AI asistan."
    />
  );

  return (
    <PageLayout hero={hero}>
      <PageBanner src="/images/pages/google-workspace.jpg" alt="Google Workspace entegrasyonu" height="360px" />
      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[28px] font-bold mb-4" style={{ color: "var(--fg)" }}>Rutin işleri AI'a devredin</h2>
            <p className="text-[15px] leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--fg)", opacity: 0.85 }}>
              Google Docs'ta rapor hazırlamak, Sheets'te veri güncellemek, Calendar'da toplantı planlamak... Bu rutin işlemleri saniyeler içinde, sesli komutla yapın.
            </p>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[28px] font-bold mb-10 text-center" style={{ color: "var(--fg)" }}>Bağlanabildiği Uygulamalar</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {integrations.map((i) => (
                <div key={i.title} className="p-6 rounded-2xl transition-colors duration-200" style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(75,111,212,0.12)", color: "var(--brand-cyan)" }}>
                    <i.icon size={22} />
                  </div>
                  <h3 className="text-[16px] font-semibold mb-2" style={{ color: "var(--fg)" }}>{i.title}</h3>
                  <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.82 }}>{i.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <img
                src="/images/pages/productivity.jpg"
                alt="Kurumsal üretkenlik araçları"
                className="w-full h-auto object-cover"
                style={{ maxHeight: "320px" }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[28px] font-bold mb-10 text-center" style={{ color: "var(--fg)" }}>Örnek Kullanım Senaryoları</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                { cmd: "Haftalık satış raporunu hazırla", result: "Google Sheets'ten verileri çeker, analiz eder ve Google Docs'ta formatlı rapor oluşturur." },
                { cmd: "Yarın 14:00'te pazarlama ekibiyle toplantı ayarla", result: "Tüm katılımcıların takvimini kontrol eder, müsaitse toplantı oluşturur, davet gönderir." },
                { cmd: "Son 3 ayda en çok şikayet alan ürünler hangileri?", result: "CRM ve yardım masası verilerini sorgular, sıralı liste ve trend grafiği hazırlar." },
                { cmd: "Slack'teki #genel kanalının bugünkü özetini çıkar", result: "Gün içindeki önemli mesajları, kararları ve aksiyon maddelerini özetler." },
                { cmd: "Jira'da bana atanmış bugün bitmesi gereken task'ları listele", result: "Jira API üzerinden filtreleme yapar, öncelik sırasına göre listeler." },
                { cmd: "Google Drive'da '2026 Bütçe' dosyasını bul ve özetle", result: "Drive'da arama yapar, dosyayı bulur, içeriğini analiz eder ve maddeler halinde özetler." },
              ].map((s, i) => (
                <div key={i} className="p-5 rounded-2xl transition-colors duration-200" style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}>
                  <p className="text-[13px] font-semibold mb-2" style={{ color: "var(--brand-cyan)" }}>🗣 "{s.cmd}"</p>
                  <p className="text-[13px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.8 }}>
                    <Check size={13} className="inline mr-1" style={{ color: "#10b981" }} />{s.result}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[24px] font-bold mb-10 text-center" style={{ color: "var(--fg)" }}>Diğer Desteklenen Uygulamalar</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {["Notion", "Trello", "Asana", "Jira", "Monday.com", "Airtable", "Dropbox", "OneDrive", "GitHub", "GitLab", "Figma", "Miro", "Confluence", "Zoom", "Teams"].map((app) => (
                <span key={app} className="px-4 py-2 rounded-full text-[13px] font-medium" style={{ background: "var(--card-bg)", border: "1px solid var(--border)", color: "var(--fg)" }}>
                  {app}
                </span>
              ))}
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6 text-center">
          <a href="https://app.verasist.ai" className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-[15px] transition-all duration-200" style={{ background: "var(--brand-navy)", color: "#fff" }}>
            Kurumsal Asistanı Keşfedin <ArrowRight size={18} />
          </a>
        </section>
      </SectionReveal>
    </PageLayout>
  );
};

export default EnterpriseAssistantPage;
